import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePos } from '../context/PosContext';
import { OnlineOrder } from '../types';
import {
  AggregatorPlatform,
  AggregatorSettings,
  PlatformConfig,
  PLATFORMS,
  platformOf,
  getAggregatorSettings,
  saveAggregatorSettings,
  computePayout,
} from '../services/aggregator';
import { formatDate } from '../services/datetime';
import './Sections.css';
import './SwiggyZomato.css';

// Swiggy & Zomato — aggregator manager (online_app_fragment.xml / the Android
// "online app"). The real app talks to the Swiggy/Zomato partner APIs from a
// server; the web port is local-first, so this screen instead manages each
// platform locally: commission / store-id / packaging / menu-sync config, and
// a payout-reconciliation dashboard (gross → commission + GST → net payout)
// computed from the online orders already captured on the Online Orders board.

type RangeKey = 'today' | 'month' | 'all';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'month', label: 'This Month' },
  { key: 'all', label: 'All Time' },
];

const inRange = (iso: string, range: RangeKey): boolean => {
  if (range === 'all') return true;
  const d = new Date(iso);
  const now = new Date();
  if (range === 'today') return d.toDateString() === now.toDateString();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

const SwiggyZomato: React.FC = () => {
  const navigate = useNavigate();
  const { onlineOrders, products, config, formatMoney, isOn } = usePos();
  // "8 — Swiggy & Zomato / B. Disable Save Button" hides the per-platform Save.
  const hideSave = isOn('disable_save_button_sz');

  const [settings, setSettings] = useState<AggregatorSettings>(() => getAggregatorSettings());
  const [range, setRange] = useState<RangeKey>('month');
  const [savedFlash, setSavedFlash] = useState<AggregatorPlatform | null>(null);

  const setField = (p: AggregatorPlatform, k: keyof PlatformConfig, v: string | number | boolean) =>
    setSettings((prev) => ({ ...prev, [p]: { ...prev[p], [k]: v } }));

  const persist = (next: AggregatorSettings, flash?: AggregatorPlatform) => {
    saveAggregatorSettings(next);
    setSettings(next);
    if (flash) {
      setSavedFlash(flash);
      setTimeout(() => setSavedFlash(null), 1500);
    }
  };

  const savePlatform = (p: AggregatorPlatform) => persist(settings, p);

  const toggleEnabled = (p: AggregatorPlatform) =>
    persist({ ...settings, [p]: { ...settings[p], enabled: !settings[p].enabled } });

  const syncMenu = (p: AggregatorPlatform) =>
    persist({ ...settings, [p]: { ...settings[p], menuSyncedCount: products.length, menuSyncedAt: new Date().toISOString() } }, p);

  // Orders grouped by platform within the selected date range.
  const byPlatform = useMemo(() => {
    const map: Record<AggregatorPlatform, OnlineOrder[]> = { Swiggy: [], Zomato: [] };
    onlineOrders.forEach((o) => {
      const p = platformOf(o.channel);
      if (p && inRange(o.createdAt, range)) map[p].push(o);
    });
    return map;
  }, [onlineOrders, range]);

  return (
    <div className="sz-page section-page">
      <div className="sz-topbar">
        <div>
          <h1 className="sz-title">Swiggy &amp; Zomato</h1>
          <p className="muted sz-sub">Aggregator commission, menu sync &amp; payout reconciliation.</p>
        </div>
        <div className="sz-topbar-right">
          <select className="sz-range" value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
            {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <button className="btn-grey" onClick={() => navigate('/online-orders')}>Open Order Board →</button>
        </div>
      </div>

      {PLATFORMS.map((p) => {
        const cfg = settings[p];
        const orders = byPlatform[p];
        const active = orders.filter((o) => o.status !== 'cancelled');
        const gross = active.reduce((s, o) => s + o.total, 0);
        const delivered = orders.filter((o) => o.status === 'delivered').length;
        const cancelled = orders.filter((o) => o.status === 'cancelled').length;
        const payout = computePayout(gross, delivered, cancelled, active.length, cfg);
        const recent = [...orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5);

        return (
          <div key={p} className={`sz-platform sz-${p.toLowerCase()} ${cfg.enabled ? '' : 'disabled'}`}>
            <div className="sz-plat-head">
              <span className="sz-plat-name">{p}</span>
              <span className="sz-plat-support muted">Support: {cfg.supportNo}</span>
              <label className="sz-switch">
                <input type="checkbox" checked={cfg.enabled} onChange={() => toggleEnabled(p)} />
                <span className="sz-slider" />
                <span className="sz-switch-lbl">{cfg.enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>

            {/* settings */}
            <div className="sz-settings">
              <label className="sz-field">Partner Store ID
                <input value={cfg.storeId} onChange={(e) => setField(p, 'storeId', e.target.value)} placeholder="e.g. 1234567" />
              </label>
              <label className="sz-field">Commission %
                <input type="number" value={cfg.commissionPercent} onChange={(e) => setField(p, 'commissionPercent', parseFloat(e.target.value) || 0)} />
              </label>
              <label className="sz-field">GST on Commission %
                <input type="number" value={cfg.gstOnCommission} onChange={(e) => setField(p, 'gstOnCommission', parseFloat(e.target.value) || 0)} />
              </label>
              <label className="sz-field">Packaging Charge
                <input type="number" value={cfg.packagingCharge} onChange={(e) => setField(p, 'packagingCharge', parseFloat(e.target.value) || 0)} />
              </label>
              {!hideSave && (
                <button className="btn-teal sz-save" onClick={() => savePlatform(p)}>
                  {savedFlash === p ? '✓ Saved' : 'Save Settings'}
                </button>
              )}
            </div>

            {/* menu sync */}
            <div className="sz-menu-sync">
              <span>
                Menu: <strong>{cfg.menuSyncedCount}</strong> / {products.length} items synced
                {cfg.menuSyncedAt && <span className="muted"> · last {formatDate(cfg.menuSyncedAt)}</span>}
              </span>
              <button className="btn-grey sz-sync-btn" onClick={() => syncMenu(p)}>Sync Menu ({products.length})</button>
            </div>

            {/* payout dashboard */}
            <div className="sz-kpis">
              <Kpi label="Orders" value={String(payout.orders)} sub={`${delivered} delivered · ${cancelled} cancelled`} />
              <Kpi label="Gross Sales" value={formatMoney(payout.gross)} />
              <Kpi label="Commission" value={`- ${formatMoney(payout.commission)}`} sub={`${cfg.commissionPercent}%`} danger />
              <Kpi label="GST on Comm." value={`- ${formatMoney(payout.gstOnCommission)}`} sub={`${cfg.gstOnCommission}%`} danger />
              <Kpi label="Net Payout" value={formatMoney(payout.netPayout)} accent />
              <Kpi label="Avg Order" value={formatMoney(payout.avgOrderValue)} />
            </div>

            {/* recent orders + new */}
            <div className="sz-recent">
              <div className="sz-recent-head">
                <span>Recent {p} orders</span>
                <button className="sz-new-link" onClick={() => navigate(`/online-orders?new=${p}`)}>+ New {p} Order</button>
              </div>
              {recent.length === 0 ? (
                <p className="muted sz-empty">No {p} orders in this period.</p>
              ) : (
                <table className="section-table">
                  <thead>
                    <tr><th>Order</th><th>Customer</th><th>Status</th><th className="right">Amount</th></tr>
                  </thead>
                  <tbody>
                    {recent.map((o) => (
                      <tr key={o.id} className="sz-recent-row" onClick={() => navigate('/online-orders')}>
                        <td>#{o.orderNo}</td>
                        <td>{o.customerName || 'Online customer'}</td>
                        <td><span className={`badge oo-status oo-status-${o.status}`}>{o.status.replace('_', ' ')}</span></td>
                        <td className="right">{formatMoney(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })}

      <p className="muted sz-foot">
        Outlet: {config.storeName || '—'} · Aggregator orders settle into real sales when accepted on the{' '}
        <button className="sz-inline-link" onClick={() => navigate('/online-orders')}>Online Orders</button> board.
      </p>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string; sub?: string; accent?: boolean; danger?: boolean }> = ({ label, value, sub, accent, danger }) => (
  <div className={`sz-kpi ${accent ? 'accent' : ''} ${danger ? 'danger' : ''}`}>
    <div className="sz-kpi-label">{label}</div>
    <div className="sz-kpi-value">{value}</div>
    {sub && <div className="sz-kpi-sub">{sub}</div>}
  </div>
);

export default SwiggyZomato;
